/**
 * debugReportCapture.buffers — console + runtime-error ring buffers.
 *
 * Two bounded in-memory buffers patched onto the global console and the
 * `window` error/unhandledrejection events. Extracted from
 * `./debugReportCapture` (file-size guard); the collector + submit/download
 * logic still live there and re-export these for back-compat.
 *
 * The console patch only records what code explicitly logs. Uncaught runtime
 * errors and (critically) unhandled promise rejections — e.g. the 127
 * "Uncaught (in promise) AbortError" a live session produced — never flow
 * through `console.*`, so they were invisible in every bundle. We hook the two
 * `window` events into a bounded ring buffer mirroring the console capture.
 */
import type {
  ConsoleEntry,
  ConsoleLevel,
  RuntimeErrorEntry,
  RuntimeErrorKind,
} from './debugReportCapture.types'

// ── Console capture ──────────────────────────────────────────────────────

const consoleBuffer: ConsoleEntry[] = []
const MAX_CONSOLE_ENTRIES = 500

// Noise patterns stripped when serialising the bundle (raw buffer kept).
const CONSOLE_NOISE_PATTERNS = [
  /^\[TwilioVoice\]\[(Device|WSTransport|AudioHelper|OutputDeviceCollection|EventPublisher|PStream|AudioProcessorEventObserver)\]/,
  /^\[workbox-/,
]

function formatConsoleArg(arg: unknown): string {
  if (typeof arg === 'string') return arg
  if (arg instanceof Error) return `${arg.name}: ${arg.message}\n${arg.stack ?? ''}`
  try {
    return JSON.stringify(arg)
  } catch {
    return String(arg)
  }
}

function pushConsole(level: ConsoleLevel, args: unknown[]): void {
  const message = args.map(formatConsoleArg).join(' ').slice(0, 2000)
  consoleBuffer.push({ level, message, timestamp: new Date().toISOString() })
  if (consoleBuffer.length > MAX_CONSOLE_ENTRIES) consoleBuffer.shift()
}

export function installConsoleCapture(): void {
  if (typeof window === 'undefined') return
  const w = window as Window & { __DEBUG_CONSOLE_PATCHED__?: boolean }
  if (w.__DEBUG_CONSOLE_PATCHED__) return
  // Dynamic aliasing avoids direct `console.*` member access so the
  // `no-console` lint rule doesn't fire. Wrapping every level is required
  // to capture the full log stream for debug bundles (not just errors).
  const c = globalThis.console as unknown as Record<ConsoleLevel, (...args: unknown[]) => void>
  const levels: ConsoleLevel[] = ['log', 'info', 'warn', 'error', 'debug']
  for (const level of levels) {
    const orig = c[level].bind(globalThis.console)
    c[level] = (...args: unknown[]) => {
      pushConsole(level, args)
      orig(...args)
    }
  }
  w.__DEBUG_CONSOLE_PATCHED__ = true
}

export function getConsoleBuffer(): ConsoleEntry[] {
  return [...consoleBuffer]
}

/** Strip well-known SDK noise when serialising a bundle. Raw buffer untouched. */
export function filterConsoleNoise(entries: ConsoleEntry[]): { signal: ConsoleEntry[]; noiseCount: number } {
  const signal: ConsoleEntry[] = []
  let noiseCount = 0
  for (const e of entries) {
    if (CONSOLE_NOISE_PATTERNS.some(p => p.test(e.message))) { noiseCount++; continue }
    signal.push(e)
  }
  return { signal, noiseCount }
}

// ── Runtime error / unhandled-rejection capture ──────────────────────────

const runtimeErrorBuffer: RuntimeErrorEntry[] = []
const MAX_RUNTIME_ERROR_ENTRIES = 200

function describeRejectionReason(reason: unknown): { message: string; stack: string | null } {
  if (reason instanceof Error) {
    return { message: `${reason.name}: ${reason.message}`, stack: reason.stack ?? null }
  }
  if (typeof reason === 'string') return { message: reason, stack: null }
  try {
    return { message: JSON.stringify(reason), stack: null }
  } catch {
    return { message: String(reason), stack: null }
  }
}

function pushRuntimeError(kind: RuntimeErrorKind, message: string, stack: string | null): void {
  runtimeErrorBuffer.push({
    kind,
    message: message.slice(0, 2000),
    stack: stack ? stack.slice(0, 4000) : null,
    timestamp: new Date().toISOString(),
  })
  if (runtimeErrorBuffer.length > MAX_RUNTIME_ERROR_ENTRIES) runtimeErrorBuffer.shift()
}

export function installRuntimeErrorCapture(): void {
  if (typeof window === 'undefined') return
  const w = window as Window & { __DEBUG_RUNTIME_ERRORS_PATCHED__?: boolean }
  if (w.__DEBUG_RUNTIME_ERRORS_PATCHED__) return
  // Passive listeners — never call preventDefault, so the browser still logs
  // the error/rejection to the real console and other handlers run unchanged.
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const { message, stack } = describeRejectionReason(event.reason)
    pushRuntimeError('unhandledrejection', message, stack)
  })
  window.addEventListener('error', (event: ErrorEvent) => {
    if (event.error instanceof Error) {
      pushRuntimeError('error', `${event.error.name}: ${event.error.message}`, event.error.stack ?? null)
    } else {
      const where = event.filename ? ` (${event.filename}:${event.lineno}:${event.colno})` : ''
      pushRuntimeError('error', `${event.message ?? 'Unknown error'}${where}`, null)
    }
  })
  w.__DEBUG_RUNTIME_ERRORS_PATCHED__ = true
}

export function getRuntimeErrorBuffer(): RuntimeErrorEntry[] {
  return [...runtimeErrorBuffer]
}
